import React, { useEffect, useRef, useState } from "react";
import * as L from "./style";
import LogoLight from "../../Assets/Images/FanxoLight.svg";
import FanxoStar from "../../Assets/Images/FanxoStar.svg";
import girlImageSix from "../../Assets/Images/Home/girlImageSix.png";
import introVideo1 from "../../Assets/intropage1.mp4";
import introVideo2 from "../../Assets/intropage2.mp4";
import introVideo3 from "../../Assets/intropage3.mp4";
import introVideo4 from "../../Assets/intropage4.mp4";
import introVideo5 from "../../Assets/intropage5.mp4";
import introPoster1 from "../../Assets/intropage1-poster.jpg";
import introPoster2 from "../../Assets/intropage2-poster.jpg";
import introPoster3 from "../../Assets/intropage3-poster.jpg";
import introPoster4 from "../../Assets/intropage4-poster.jpg";
import introPoster5 from "../../Assets/intropage5-poster.jpg";

import { useNavigate } from "react-router-dom";

interface LandingReel {
  id: number;
  videoURL: string;
  posterURL: string;
}

const REEL_ROTATION_MS = 2400;
const ACTIVE_REEL_PLAYBACK_RATE = 1.05;
const LANDING_EARNINGS_TOTAL = 124680;

const playLandingReel = (video: HTMLVideoElement | null) => {
  if (!video) return;

  video.muted = true;
  video.loop = true;
  video.playsInline = true;
  video.playbackRate = ACTIVE_REEL_PLAYBACK_RATE;
  video.play().catch(() => undefined);
};

const landingReels: LandingReel[] = [
  { id: 1, videoURL: introVideo1, posterURL: introPoster1 },
  { id: 2, videoURL: introVideo3, posterURL: introPoster3 },
  { id: 3, videoURL: introVideo2, posterURL: introPoster2 },
  { id: 4, videoURL: introVideo4, posterURL: introPoster4 },
  { id: 5, videoURL: introVideo5, posterURL: introPoster5 },
];

const statLinePoints = "10,118 58,104 106,112 154,74 202,86 250,42 298,54 346,28";
const statFeatureLabels = ["Subscribers", "Paid DMs", "Exclusive Content", "Earnings"];
const FEATURE_TAPE_TEXT =
  "\u2022\u00A0\u00A0Subscriptions\u00A0\u00A0\u2022\u00A0\u00A0Paid DMs\u00A0\u00A0\u2022\u00A0\u00A0Exclusive Content\u00A0\u00A0\u2022\u00A0\u00A0Live Streams\u00A0\u00A0\u2022\u00A0\u00A0Brand-Safe Monetization";

const getReelOffset = (index: number, activeIndex: number, total: number) => {
  const rawOffset = index - activeIndex;
  if (rawOffset > total / 2) return rawOffset - total;
  if (rawOffset < -total / 2) return rawOffset + total;
  return rawOffset;
};
const Landing: React.FC = () => {
  const navigate = useNavigate();

  const [activeReelIndex, setActiveReelIndex] = useState(0);
  const [isRiftonReady, setIsRiftonReady] = useState(false);
  const [displayEarnings, setDisplayEarnings] = useState(0);
  const [statsInView, setStatsInView] = useState(false);
  const statsCardRef = useRef<HTMLDivElement | null>(null);
  const reelVideoRefs = useRef<Array<HTMLVideoElement | null>>([]);

  const items = [
    {
      key: "1",
      label: "What is Fanxo? How does it work?",
      children: (
        <p>
          Fanxo helps creators monetize by turning their free fan following into
          premium fans. Creators set up account, add their fanxo link to their
          social media link-in-bio (like Instagram or YouTube). Fans sign up as
          premium members and get access to exclusive content
        </p>
      ),
    },
    {
      key: "2",
      label: " I am a creator. How do I set up my account?",
      children: (
        <p>
          Creators fill up the creator application form, provide their social
          media links, our team will review and accept them into the platform.
          For any assistance, you can contact us at business@fanxo.club.
        </p>
      ),
    },
    {
      key: "3",
      label: "Is there a limit to what I can earn?",
      children: (
        <p>
          No, there’s no limit! Your earning potential completely depends on you
          and your following
        </p>
      ),
    },
  ];

  useEffect(() => {
    let isMounted = true;
    const fallbackTimer = window.setTimeout(() => {
      if (isMounted) setIsRiftonReady(true);
    }, 700);

    if ("fonts" in document) {
      document.fonts
        .load("1em RiftonNorm")
        .then(() => document.fonts.ready)
        .then(() => {
          if (isMounted) setIsRiftonReady(true);
        })
        .catch(() => {
          if (isMounted) setIsRiftonReady(true);
        })
        .finally(() => window.clearTimeout(fallbackTimer));
    }

    return () => {
      isMounted = false;
      window.clearTimeout(fallbackTimer);
    };
  }, []);

  useEffect(() => {
    const rotation = window.setInterval(() => {
      setActiveReelIndex((current) => (current + 1) % landingReels.length);
    }, REEL_ROTATION_MS);

    return () => window.clearInterval(rotation);
  }, []);

  useEffect(() => {
    const card = statsCardRef.current;
    if (!card) return;

    if (!("IntersectionObserver" in window)) {
      setStatsInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(card);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!statsInView) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion) {
      setDisplayEarnings(LANDING_EARNINGS_TOTAL);
      return;
    }

    let frameId = 0;
    const duration = 1400;
    const startedAt = performance.now();

    const tick = (timestamp: number) => {
      const progress = Math.min((timestamp - startedAt) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      setDisplayEarnings(Math.round(LANDING_EARNINGS_TOTAL * easedProgress));

      if (progress < 1) {
        frameId = window.requestAnimationFrame(tick);
      }
    };

    frameId = window.requestAnimationFrame(tick);

    return () => window.cancelAnimationFrame(frameId);
  }, [statsInView]);

  useEffect(() => {
    reelVideoRefs.current.forEach((video, index) => {
      if (!video) return;

      if (index === activeReelIndex) {
        playLandingReel(video);
      } else {
        video.pause();
        video.playbackRate = 1;
        video.currentTime = 0;
      }
    });
  }, [activeReelIndex]);

  return (
    <L.Landing>
      <L.Header>
        <L.BrandLockup>
          <L.BrandMark src={FanxoStar} alt="" aria-hidden="true" />
          <L.Logo src={LogoLight} alt="Fanxo" />
        </L.BrandLockup>
        <div className="flex items-center gap-4">
          <>
            <L.Login className="font-noto" to={"/login"}>
              Log in
            </L.Login>
            <L.SignUp className="font-noto" to={"/signup"}>
              Get Started
            </L.SignUp>
          </>
        </div>
      </L.Header>

      <L.SectionOne>
        <h3 className={`font-rifton-norm ${isRiftonReady ? "rifton-ready" : ""}`}>
          <span className="hero-line">EARN AS A CREATOR</span>
          <span className="hero-amp">&</span>
          <span className="hero-line">CONNECT WITH YOUR TRUE FANS</span>
        </h3>
        <L.HeroCopy>
          <strong>#Build your subscription community on a brand-safe platform.</strong>
        </L.HeroCopy>
        <L.FeatureTape aria-label={FEATURE_TAPE_TEXT}>
          <L.FeatureTapeTrack aria-hidden="true">
            {[0, 1, 2].map((item) => (
              <span key={item}>{FEATURE_TAPE_TEXT}</span>
            ))}
          </L.FeatureTapeTrack>
        </L.FeatureTape>
        <L.CreatorBtn
          onClick={() => navigate("/signup/creator")}
          className="font-rifton-norm"
        >
          sign up as a creator
        </L.CreatorBtn>
        <L.HeroReelShowcase aria-label="Fanxo creator reels preview">
          {landingReels.map((reel, index) => {
            const offset = getReelOffset(index, activeReelIndex, landingReels.length);
            const clampedOffset = Math.max(-2, Math.min(2, offset));
            const isActive = index === activeReelIndex;

            return (
              <L.HeroReel
                key={reel.id}
                $offset={clampedOffset}
                $active={isActive}
                aria-hidden={!isActive}
              >
                <L.HeroReelVideo
                  ref={(element) => {
                    reelVideoRefs.current[index] = element;
                  }}
                  src={reel.videoURL}
                  poster={reel.posterURL}
                  muted
                  loop
                  playsInline
                  preload={isActive ? "auto" : "metadata"}
                  onCanPlay={() => {
                    if (isActive) {
                      playLandingReel(reelVideoRefs.current[index]);
                    }
                  }}
                />
              </L.HeroReel>
            );
          })}
        </L.HeroReelShowcase>
      </L.SectionOne>
      <L.SectionThree>
        <L.SubHeading className="font-rifton-norm">
          Earnings as you have <br />
          never seen
        </L.SubHeading>
        <L.SecThreeImg src={girlImageSix} alt="user" crossOrigin="anonymous" />
      </L.SectionThree>

      <L.SectionFour>
        <L.SubHeading className="font-rifton-norm">
          TRACK YOUR STATS
        </L.SubHeading>
        <L.SectionFlex>
          <L.EarningsCard ref={statsCardRef} aria-label="Creator earnings and stats preview">
            <L.EarningsCardHeader>
              <span>This month's earnings</span>
              <L.GrowthChip>+18% vs last month</L.GrowthChip>
            </L.EarningsCardHeader>
            <L.EarningsValue>
              {displayEarnings.toLocaleString("en-IN", {
                style: "currency",
                currency: "INR",
                maximumFractionDigits: 0,
              })}
            </L.EarningsValue>
            <L.LineChart $active={statsInView} aria-hidden="true">
              <svg viewBox="0 0 356 140" role="img" focusable="false">
                <defs>
                  <linearGradient id="statsLineGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#2387ff" />
                    <stop offset="58%" stopColor="#d888e8" />
                    <stop offset="100%" stopColor="#ff941f" />
                  </linearGradient>
                  <linearGradient id="statsAreaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#d888e8" stopOpacity="0.24" />
                    <stop offset="100%" stopColor="#2387ff" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path className="stats-area" d={`M ${statLinePoints} L 346,130 L 10,130 Z`} />
                <polyline className="stats-line" points={statLinePoints} pathLength="1" />
                <circle className="stats-dot" cx="346" cy="28" r="4.5" />
              </svg>
            </L.LineChart>
          </L.EarningsCard>
          <L.StatsFeatureGrid aria-label="Creator stats categories">
            {statFeatureLabels.map((label) => (
              <L.StatsFeature key={label}>{label}</L.StatsFeature>
            ))}
          </L.StatsFeatureGrid>
        </L.SectionFlex>
      </L.SectionFour>

      <L.FaqWrapper>
        <L.SubHeading className="font-rifton-norm">
          FREQUENTLY ASKED <br /> QUESTIONS
        </L.SubHeading>
        <L.CollaspeWrapper>
          <L.Collapse expandIconPosition="right" accordion items={items} />
        </L.CollaspeWrapper>
      </L.FaqWrapper>

      <L.Footer>
        <L.FooterItem to="/about-us" className="font-rifton-norm">
          About Us
        </L.FooterItem>
        <L.FooterItem to="/terms-and-conditions" className="font-rifton-norm">
          Terms and conditions
        </L.FooterItem>
        <L.FooterItem to="/privacy-policy" className="font-rifton-norm">
          Privacy policy
        </L.FooterItem>
        <L.FooterItem to="/faqs" className="font-rifton-norm">
          Faq
        </L.FooterItem>
      </L.Footer>
    </L.Landing>
  );
};

export default Landing;
